import type { DialogNode } from "./dialog-data";

export interface CueCard {
  id: string;
  type: DialogNode["type"];
  text: string;
  translation: string;
  vocalizationTip?: string;
  branchLabel: string;
  branchIndex: number;
  hasPlaceholder: boolean;
  placeholders: string[];
}

/** A response option the user can say */
export interface ResponseOption {
  id: string;
  text: string;
  translation: string;
  vocalizationTip?: string;
  hasPlaceholder: boolean;
  placeholders: string[];
}

/** An exchange card pairing "they say" prompt with "you say" response + alternatives */
export interface ExchangeCard {
  id: string;
  prompt: {
    text: string;
    translation: string;
    vocalizationTip?: string;
  };
  response: ResponseOption | null;
  alternatives: Omit<ResponseOption, "hasPlaceholder" | "placeholders">[];
  branchLabel: string;
  branchIndex: number;
}

const PLACEHOLDER_RE = /\[([A-Z_]+)\]/g;

export function detectPlaceholders(text: string): string[] {
  const matches: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = PLACEHOLDER_RE.exec(text)) !== null) {
    if (match[1]) matches.push(match[1]);
  }
  PLACEHOLDER_RE.lastIndex = 0;
  return matches;
}

export function flattenDialogToCards(
  nodes: DialogNode[],
  selectedChildMap: Map<string, string>
): CueCard[] {
  if (nodes.length === 0) return [];

  const byId = new Map(nodes.map((n) => [n.id, n]));
  const startNode = nodes.find((n) => n.type === "start");
  if (!startNode) return [];

  const cards: CueCard[] = [];
  const visited = new Set<string>();
  let branchIndex = 0;
  let currentBranchLabel = "";

  function walk(nodeId: string) {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);

    const node = byId.get(nodeId);
    if (!node) return;

    if (node.type === "start" || (node.children && node.children.length > 1)) {
      currentBranchLabel = node.text.slice(0, 30) + (node.text.length > 30 ? "..." : "");
      branchIndex = cards.length === 0 ? 0 : branchIndex + 1;
    }

    const placeholders = detectPlaceholders(node.text);
    cards.push({
      id: node.id,
      type: node.type,
      text: node.text,
      translation: node.translation,
      vocalizationTip: node.vocalizationTip,
      branchLabel: currentBranchLabel,
      branchIndex,
      hasPlaceholder: placeholders.length > 0,
      placeholders,
    });

    if (!node.children?.length) return;

    const selectedId = selectedChildMap.get(node.id);
    const firstChild = node.children[0];
    if (!firstChild) return;
    const nextId = selectedId && byId.has(selectedId) ? selectedId : firstChild.id;
    walk(nextId);
  }

  walk(startNode.id);
  return cards;
}

/**
 * Flatten dialog tree into exchange cards pairing "they say" with "you say".
 * Each card shows the prompt (them), your selected response, and alternative responses.
 */
export function flattenDialogToExchanges(
  nodes: DialogNode[],
  selectedChildMap: Map<string, string>
): ExchangeCard[] {
  if (nodes.length === 0) return [];

  const byId = new Map(nodes.map((n) => [n.id, n]));
  const startNode = nodes.find((n) => n.type === "start");
  if (!startNode) return [];

  const exchanges: ExchangeCard[] = [];
  const visited = new Set<string>();
  let branchIndex = 0;
  let currentBranchLabel = "";

  function walkPrompt(promptNode: DialogNode) {
    if (visited.has(promptNode.id)) return;
    visited.add(promptNode.id);

    // Get children — these are the user's response options
    const options = (promptNode.children || [])
      .map((edge) => byId.get(edge.id))
      .filter((n): n is DialogNode => n !== undefined);

    // Update branch when multiple options exist
    if (promptNode.type === "start" || options.length > 1) {
      currentBranchLabel =
        promptNode.text.slice(0, 30) + (promptNode.text.length > 30 ? "..." : "");
      branchIndex = exchanges.length === 0 ? 0 : branchIndex + 1;
    }

    if (options.length === 0) {
      // Terminal node (END) — card with just prompt, no response
      exchanges.push({
        id: promptNode.id,
        prompt: { text: promptNode.text, translation: promptNode.translation, vocalizationTip: promptNode.vocalizationTip },
        response: null,
        alternatives: [],
        branchLabel: currentBranchLabel,
        branchIndex,
      });
      return;
    }

    // Find the selected response option
    const selectedId = selectedChildMap.get(promptNode.id);
    const firstOption = options[0]!;
    const selected = (selectedId && byId.get(selectedId)) || firstOption;

    // Sibling options as alternatives
    const siblingAlts = options
      .filter((o) => o.id !== selected.id)
      .map((o) => ({
        id: o.id,
        text: o.text,
        translation: o.translation,
        vocalizationTip: o.vocalizationTip,
      }));

    // PhraseVariant alternatives from the selected node
    const phraseAlts = (selected.alternatives || []).map((a, i) => ({
      id: `${selected.id}-pv-${i}`,
      text: a.text,
      translation: a.translation,
      vocalizationTip: a.vocalizationTip,
    }));

    const placeholders = detectPlaceholders(selected.text);

    exchanges.push({
      id: `${promptNode.id}+${selected.id}`,
      prompt: { text: promptNode.text, translation: promptNode.translation, vocalizationTip: promptNode.vocalizationTip },
      response: {
        id: selected.id,
        text: selected.text,
        translation: selected.translation,
        vocalizationTip: selected.vocalizationTip,
        hasPlaceholder: placeholders.length > 0,
        placeholders,
      },
      alternatives: [...siblingAlts, ...phraseAlts],
      branchLabel: currentBranchLabel,
      branchIndex,
    });

    // Follow selected option's children (them responses)
    visited.add(selected.id);
    const responses = (selected.children || [])
      .map((edge) => byId.get(edge.id))
      .filter((n): n is DialogNode => n !== undefined);

    if (responses.length > 0) {
      const nextSelectedId = selectedChildMap.get(selected.id);
      const firstResp = responses[0]!;
      const nextPrompt = (nextSelectedId && byId.get(nextSelectedId)) || firstResp;
      walkPrompt(nextPrompt);
    }
  }

  walkPrompt(startNode);
  return exchanges;
}

export interface Branch {
  label: string;
  index: number;
  startCardIndex: number;
}

export function extractBranches(
  cards: Array<{ branchIndex: number; branchLabel: string }>
): Branch[] {
  const branches: Branch[] = [];
  let lastBranchIndex = -1;
  for (let i = 0; i < cards.length; i++) {
    const card = cards[i]!;
    if (card.branchIndex !== lastBranchIndex) {
      lastBranchIndex = card.branchIndex;
      branches.push({
        label: card.branchLabel,
        index: card.branchIndex,
        startCardIndex: i,
      });
    }
  }
  return branches;
}

export function replacePlaceholders(
  text: string,
  details: Record<string, string>
): string {
  return text.replace(PLACEHOLDER_RE, (match, key) => {
    return details[key] ?? match;
  });
}
